import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Truck, Search, Download, Plus, ArrowLeft, Eye, Trash2, List, Grid, Users, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import RowActions from '../../components/RowActions';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import { renderCell, coalesce, renderCellDate } from '../../utils/helpers';
import { EXTINGUISHER_TYPES } from '../../utils/constants';

const VehicleExtinguishers = () => {
  const [view, setView] = useState('list');
  const [viewMode, setViewMode] = useState('table');
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [equipment, setEquipment] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filterStructure, setFilterStructure] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState({ open: false, item: null });
  const [formData, setFormData] = useState({
    clientId: '',
    siteId: '',
    serialNumber: '',
    structure: '',
    affectation: '',
    vehicleType: '',
    vehicleRegistration: '',
    extinguisherType: '',
    detailedType: '',
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    recoveryDate: '',
    recoveredBy: '',
  });
  
  const { success: showSuccess, error: showError } = useToast();

  useEffect(() => {
    loadClients();
    loadAllEquipment();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadClientEquipment(selectedClient.id);
    }
  }, [selectedClient]);

  useEffect(() => {
    applyFilters();
  }, [equipment, searchQuery, filterStructure]);

  useEffect(() => {
    filterClients();
  }, [searchQuery, clients, view]);

  useEffect(() => {
    if (formData.clientId) {
      loadSites(formData.clientId);
    }
  }, [formData.clientId]);

  const loadClients = async () => {
    try {
      const data = await apiService.getClients();
      setClients(data);
      setFilteredClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllEquipment = async () => {
    try {
      const data = await apiService.getVehicleEquipment(null, null);
      setAllEquipment(data);
    } catch (error) {
      console.error('Failed to load equipment:', error);
    }
  };

  const loadClientEquipment = async (clientId) => {
    try {
      setLoading(true);
      const data = await apiService.getVehicleEquipment(clientId, null);
      setEquipment(data);
    } catch (error) {
      showError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async (clientId) => {
    try {
      const data = await apiService.getSitesByClient(clientId);
      setSites(data);
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const filterClients = () => {
    if (view !== 'list' || !searchQuery) {
      setFilteredClients(clients);
      return;
    }
    const query = searchQuery.toLowerCase();
    setFilteredClients(clients.filter(c => c.name?.toLowerCase().includes(query) || c.company?.toLowerCase().includes(query)));
  };

  const getClientSummary = (clientId) => {
    const clientEquipment = allEquipment.filter(e => e.clientId === clientId);
    const controlled = clientEquipment.filter(e => e.lastMaintenanceDate).length;
    const toControl = clientEquipment.filter(e => !e.lastMaintenanceDate).length;
    return { total: clientEquipment.length, controlled, toControl, hasData: clientEquipment.length > 0 };
  };

  const totalClients = clients.length;
  const clientsWithData = clients.filter(c => getClientSummary(c.id).hasData).length;
  const totalVehicles = allEquipment.length;
  const totalControlled = allEquipment.filter(e => e.lastMaintenanceDate).length;

  const applyFilters = () => {
    let filtered = equipment;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.vehicleRegistration?.toLowerCase().includes(query) ||
        item.serialNumber?.toLowerCase().includes(query) ||
        item.affectation?.toLowerCase().includes(query)
      );
    }
    if (filterStructure) {
      filtered = filtered.filter(item => item.structure === filterStructure);
    }
    setFilteredEquipment(filtered);
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setView('detail');
    setSearchQuery('');
    setFilterStructure('');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedClient(null);
    setEquipment([]);
    setFilteredEquipment([]);
    setSearchQuery('');
    loadAllEquipment();
  };

  const handleResetClient = async (client) => {
    if (!window.confirm(`⚠️ Supprimer TOUS les équipements véhicules pour "${client.name}"?`)) return;
    try {
      const clientEquipment = allEquipment.filter(e => e.clientId === client.id);
      for (const eq of clientEquipment) await apiService.deleteEquipment(eq.id);
      showSuccess(`Équipements supprimés pour ${client.name}`);
      loadAllEquipment();
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const buildFormFromItem = (item = {}) => ({
    clientId: item.clientId || selectedClient?.id || '',
    siteId: item.siteId || '',
    serialNumber: item.serialNumber || '',
    structure: item.structure || '',
    affectation: item.affectation || '',
    vehicleType: item.vehicleType || '',
    vehicleRegistration: item.vehicleRegistration || '',
    extinguisherType: item.extinguisherType || '',
    detailedType: item.detailedType || '',
    lastMaintenanceDate: item.lastMaintenanceDate ? item.lastMaintenanceDate.split('T')[0] : '',
    nextMaintenanceDate: item.nextMaintenanceDate ? item.nextMaintenanceDate.split('T')[0] : '',
    recoveryDate: item.recoveryDate ? item.recoveryDate.split('T')[0] : '',
    recoveredBy: item.recoveredBy || '',
  });

  const openAddModal = () => {
    setEditingItem(null);
    setFormData(buildFormFromItem());
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData(buildFormFromItem(item));
    setShowModal(true);
  };

  const openViewModal = (item) => setViewItem(item);

  const handleDeleteItem = async () => {
    if (!deleteItemConfirm.item) return;
    try {
      await apiService.deleteEquipment(deleteItemConfirm.item.id);
      showSuccess('Équipement supprimé');
      setDeleteItemConfirm({ open: false, item: null });
      if (selectedClient) loadClientEquipment(selectedClient.id);
      loadAllEquipment();
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'lastMaintenanceDate' && value) {
      const lastDate = new Date(value);
      lastDate.setMonth(lastDate.getMonth() + 6);
      setFormData({ ...formData, [name]: value, nextMaintenanceDate: lastDate.toISOString().split('T')[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { type: 'extinguisher', ...formData, capacity: '1' };
      const result = editingItem
        ? await apiService.updateEquipment(editingItem.id, payload)
        : await apiService.createEquipment(payload);
      if (result.success !== false) {
        showSuccess(editingItem ? 'Équipement mis à jour' : 'Équipement ajouté');
        setShowModal(false);
        setEditingItem(null);
        if (selectedClient) loadClientEquipment(selectedClient.id);
        loadAllEquipment();
      } else {
        showError(result.message || 'Erreur');
      }
    } catch (error) {
      showError(error.message?.includes('Serial number') ? `N° série "${formData.serialNumber}" existe déjà` : error.message);
    }
  };

  const columns = [
    {
      header: 'Client',
      accessor: 'name',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg"><Truck className="w-5 h-5 text-red-600" /></div>
          <div><p className="font-semibold text-gray-900">{row.name}</p><p className="text-sm text-gray-500">{row.company}</p></div>
        </div>
      ),
    },
    {
      header: 'Véhicules',
      accessor: 'total',
      render: (row) => {
        const s = getClientSummary(row.id);
        return <span className={`px-3 py-1 rounded-full text-sm font-bold ${s.total > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>{s.total}</span>;
      },
    },
    {
      header: 'Contrôlés',
      accessor: 'controlled',
      render: (row) => {
        const s = getClientSummary(row.id);
        return <span className={`px-3 py-1 rounded-full text-sm font-bold ${s.controlled > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>{s.controlled}</span>;
      },
    },
    {
      header: 'À contrôler',
      accessor: 'toControl',
      render: (row) => {
        const s = getClientSummary(row.id);
        return <span className={`px-3 py-1 rounded-full text-sm font-bold ${s.toControl > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'}`}>{s.toControl}</span>;
      },
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (row) => {
        const s = getClientSummary(row.id);
        return (
          <RowActions
            onView={() => handleViewClient(row)}
            onEdit={() => handleViewClient(row)}
            onDelete={() => handleResetClient(row)}
            disableDelete={!s.hasData}
          />
        );
      },
    },
  ];

  const structures = [...new Set(equipment.map(e => e.structure).filter(Boolean))];

  if (loading && view === 'list' && clients.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div></div>;
  }

  if (view === 'list') {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3"><Truck className="w-8 h-8 text-red-600" />Équipements Véhicules</h1>
            <p className="text-gray-600 mt-1">Gérez les extincteurs véhicules par client</p>
          </div>
          <div className="flex gap-2">
            <Button variant={viewMode === 'table' ? 'primary' : 'outline'} size="sm" icon={List} onClick={() => setViewMode('table')}>Tableau</Button>
            <Button variant={viewMode === 'card' ? 'primary' : 'outline'} size="sm" icon={Grid} onClick={() => setViewMode('card')}>Cartes</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-3"><div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg"><Users className="w-5 h-5 text-white" /></div></div>
            <p className="text-sm font-bold text-gray-500 uppercase">Total Clients</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalClients}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-3"><div className="p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg"><Truck className="w-5 h-5 text-white" /></div><span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">{totalClients > 0 ? Math.round((clientsWithData / totalClients) * 100) : 0}%</span></div>
            <p className="text-sm font-bold text-gray-500 uppercase">Avec données</p>
            <p className="text-3xl font-extrabold text-gray-900">{clientsWithData}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-3"><div className="p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg"><Truck className="w-5 h-5 text-white" /></div></div>
            <p className="text-sm font-bold text-gray-500 uppercase">Total Véhicules</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalVehicles}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-3"><div className="p-2.5 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg"><CheckCircle className="w-5 h-5 text-white" /></div></div>
            <p className="text-sm font-bold text-gray-500 uppercase">Contrôlés</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalControlled}</p>
          </motion.div>
        </div>

        <Card>
          <div className="flex items-center space-x-4">
            <div className="flex-1"><Input placeholder="Rechercher un client..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} icon={Search} /></div>
            <Button variant="outline">Filtres</Button>
          </div>
        </Card>

        {viewMode === 'table' && (
          <Card>
            <div className="mb-4"><h2 className="text-lg font-semibold text-gray-900">{filteredClients.length} client(s)</h2></div>
            <Table columns={columns} data={filteredClients} />
          </Card>
        )}

        {viewMode === 'card' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client, index) => {
              const s = getClientSummary(client.id);
              return (
                <motion.div key={client.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                  <Card className="hover:shadow-lg transition-shadow">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-3 rounded-lg bg-red-100"><Truck className="w-5 h-5 text-red-600" /></div>
                          <div><h3 className="text-lg font-bold text-gray-900">{client.name}</h3><p className="text-sm text-gray-500">{client.company}</p></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-blue-50 rounded-lg p-2"><p className="text-xl font-bold text-blue-600">{s.total}</p><p className="text-xs text-gray-500">Véhicules</p></div>
                        <div className="bg-green-50 rounded-lg p-2"><p className="text-xl font-bold text-green-600">{s.controlled}</p><p className="text-xs text-gray-500">Contrôlés</p></div>
                        <div className="bg-amber-50 rounded-lg p-2"><p className="text-xl font-bold text-amber-600">{s.toControl}</p><p className="text-xs text-gray-500">À contrôler</p></div>
                      </div>
                      <div className="flex items-center justify-end pt-2 border-t border-gray-200">
                        <RowActions
                          onView={() => handleViewClient(client)}
                          onEdit={() => handleViewClient(client)}
                          onDelete={() => handleResetClient(client)}
                          disableDelete={!s.hasData}
                        />
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Detail View
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="outline" icon={ArrowLeft} onClick={handleBackToList} className="mb-4">Retour</Button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3"><Truck className="w-8 h-8 text-red-600" />{selectedClient?.name}</h1>
          <p className="text-gray-600 mt-1">{selectedClient?.company} - Extincteurs véhicules</p>
        </div>
        <div className="flex gap-3">
          <Button variant="primary" icon={Plus} onClick={openAddModal}>Ajouter</Button>
          <Button variant="secondary" icon={Download}>Exporter</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><div className="text-center"><p className="text-3xl font-bold text-blue-600">{filteredEquipment.length}</p><p className="text-sm text-gray-600">Total</p></div></Card>
        <Card><div className="text-center"><p className="text-3xl font-bold text-green-600">{filteredEquipment.filter(e => e.lastMaintenanceDate).length}</p><p className="text-sm text-gray-600">Contrôlés</p></div></Card>
        <Card><div className="text-center"><p className="text-3xl font-bold text-amber-600">{filteredEquipment.filter(e => !e.lastMaintenanceDate).length}</p><p className="text-sm text-gray-600">À contrôler</p></div></Card>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} icon={Search} />
          <Select value={filterStructure} onChange={(e) => setFilterStructure(e.target.value)} placeholder="Toutes structures" options={structures.map(s => ({ value: s, label: s }))} />
        </div>
      </Card>

      <Card>
        {loading ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div></div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">N°</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Structure</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Affectation</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Véhicule</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Matricule</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Extincteur</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Contrôle</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Prochaine</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Récupération</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Par</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase sticky right-0 z-20 bg-gray-50 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.12)]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEquipment.length === 0 ? (
                  <tr><td colSpan="11" className="px-6 py-12 text-center"><Truck className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="font-medium text-gray-900">Aucun équipement</p><Button variant="primary" icon={Plus} onClick={openAddModal} className="mt-3">Ajouter</Button></td></tr>
                ) : filteredEquipment.map((item, idx) => (
                  <motion.tr key={item.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.02 }} className="group hover:bg-gray-50">
                    <td className="px-3 py-3 text-sm text-center">{idx + 1}</td>
                    <td className="px-3 py-3 text-sm">{renderCell(item.structure)}</td>
                    <td className="px-3 py-3 text-sm">{renderCell(item.affectation)}</td>
                    <td className="px-3 py-3 text-sm">{renderCell(item.vehicleType)}</td>
                    <td className="px-3 py-3 text-sm font-mono">{renderCell(item.vehicleRegistration)}</td>
                    <td className="px-3 py-3 text-sm">{renderCell(coalesce(item.detailedType, item.extinguisherType))}</td>
                    <td className="px-3 py-3 text-sm text-center">{renderCellDate(item.lastMaintenanceDate)}</td>
                    <td className="px-3 py-3 text-sm text-center">{renderCellDate(item.nextMaintenanceDate)}</td>
                    <td className="px-3 py-3 text-sm text-center">{renderCellDate(item.recoveryDate)}</td>
                    <td className="px-3 py-3 text-sm">{renderCell(item.recoveredBy)}</td>
                    <td className="px-3 py-3 sticky right-0 z-10 bg-white group-hover:bg-gray-50 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.12)]">
                      <RowActions
                        onView={() => openViewModal(item)}
                        onEdit={() => openEditModal(item)}
                        onDelete={() => setDeleteItemConfirm({ open: true, item })}
                      />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditingItem(null); }} title={editingItem ? 'Modifier Équipement Véhicule' : 'Ajouter Équipement Véhicule'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-600">Client:</p><p className="font-semibold">{selectedClient?.name} - {selectedClient?.company}</p></div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Site" name="siteId" value={formData.siteId} onChange={handleFormChange} options={sites.map(s => ({ value: s.id, label: s.name }))} required />
            <Input label="N° Série" name="serialNumber" value={formData.serialNumber} onChange={handleFormChange} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Structure" name="structure" value={formData.structure} onChange={handleFormChange} placeholder="Ex: PNDC/DDO" required />
            <Input label="Affectation" name="affectation" value={formData.affectation} onChange={handleFormChange} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Type véhicule" name="vehicleType" value={formData.vehicleType} onChange={handleFormChange} required />
            <Input label="Matricule" name="vehicleRegistration" value={formData.vehicleRegistration} onChange={handleFormChange} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Type extincteur" name="extinguisherType" value={formData.extinguisherType} onChange={handleFormChange} options={Object.values(EXTINGUISHER_TYPES).map(t => ({ value: t, label: t }))} required />
            <Input label="Type détaillé" name="detailedType" value={formData.detailedType} onChange={handleFormChange} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Date contrôle" type="date" name="lastMaintenanceDate" value={formData.lastMaintenanceDate} onChange={handleFormChange} required />
            <Input label="Prochaine" type="date" name="nextMaintenanceDate" value={formData.nextMaintenanceDate} onChange={handleFormChange} disabled />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Date récupération" type="date" name="recoveryDate" value={formData.recoveryDate} onChange={handleFormChange} />
            <Input label="Récupéré par" name="recoveredBy" value={formData.recoveredBy} onChange={handleFormChange} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button type="submit" variant="primary">Enregistrer</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="Détails équipement véhicule">
        {viewItem && (
          <div className="space-y-3 text-sm">
            <p><span className="font-semibold">N° série:</span> {viewItem.serialNumber}</p>
            <p><span className="font-semibold">Structure:</span> {renderCell(viewItem.structure)}</p>
            <p><span className="font-semibold">Affectation:</span> {renderCell(viewItem.affectation)}</p>
            <p><span className="font-semibold">Véhicule:</span> {renderCell(viewItem.vehicleType)}</p>
            <p><span className="font-semibold">Matricule:</span> {renderCell(viewItem.vehicleRegistration)}</p>
            <p><span className="font-semibold">Extincteur:</span> {renderCell(coalesce(viewItem.detailedType, viewItem.extinguisherType))}</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setViewItem(null)}>Fermer</Button>
              <Button variant="primary" onClick={() => { openEditModal(viewItem); setViewItem(null); }}>Modifier</Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={deleteItemConfirm.open}
        onClose={() => setDeleteItemConfirm({ open: false, item: null })}
        onConfirm={handleDeleteItem}
        title="Supprimer l'équipement"
        message={`Supprimer l'équipement ${deleteItemConfirm.item?.serialNumber} ?`}
        confirmText="Supprimer"
        variant="danger"
      />
    </div>
  );
};

export default VehicleExtinguishers;
